import { ProductWithoutImage } from "../product-item-without-image";
import { ProductWithImage } from "../product-item-with-image";
import React, { useRef, useEffect, useState } from "react";
import { assets } from "../../assets";
import s from "./index.module.css";
import { Loader } from "..";

export function ProductList({ data, status, searchValue, shopInfo }) {
  const theme =
    new URLSearchParams(window.location.search).get("theme") || "with_image";

  // Filter products
  const filteredProducts = data.filter(
    (product) =>
      product?.name?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
      product?.long_desc?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
      product?.short_desc?.toLowerCase()?.includes(searchValue?.toLowerCase()),
  );

  // Group by category
  const grouped = filteredProducts.reduce((acc, product) => {
    const cat = product.category || "Uncategorized";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(product);
    return acc;
  }, {});

  const categories = Object.keys(grouped);
  const categoryRefs = useRef({});
  const tabRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState(categories[0]);
  const [isSticky, setIsSticky] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);

  const scrollToCategory = (category) => {
    const el = categoryRefs.current[category];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const titleBottom = document
        .querySelector(`.${s.heading}`)
        ?.getBoundingClientRect()?.bottom;
      const tabTop = tabRef.current?.getBoundingClientRect()?.top;

      setIsSticky(tabTop <= 0);

      if (titleBottom > 0) return;

      const positions = categories.map((cat) => {
        const el = categoryRefs.current[cat];
        return {
          cat,
          top: el?.getBoundingClientRect().top ?? Infinity,
        };
      });

      const inView = positions.find(
        (p) => p.top >= 0 && p.top < window.innerHeight / 2,
      );
      if (inView && inView.cat !== activeCategory) {
        setActiveCategory(inView.cat);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [categories, activeCategory]);

  // Loaders & Errors
  if (status === "GET_PRODUCTS_OF_PUBLIC_SHOP_REQUEST")
    return <Loader width={100} height={100} />;
  if (status === "GET_PRODUCTS_OF_PUBLIC_SHOP_ERROR") {
    return (
      <div className={s.errorDataArea}>
        <img src={assets.errorData} alt="" className={s.errorDataImage} />
      </div>
    );
  }
  if (filteredProducts.length === 0) {
    return (
      <div className={s.noProductsArea}>
        <img src={assets.noProducts} alt="" className={s.noProductsImage} />
        <p className={s.noProductsText}>
          {!searchValue
            ? "The shop is out of products right now. But hang tight — we’re restocking soon with some great stuff for you!"
            : "The product you are looking for is not available right now."}
        </p>
      </div>
    );
  }

  return (
    <div className={s.productList}>
      <h2 className={s.heading}>Our Products</h2>

      {/* Original position for scroll tracking */}
      <div ref={tabRef} className={s.categoryTabsOriginal}>
        {categories.map((cat) => (
          <button
            key={cat}
            className={`${s.tabButton} ${
              activeCategory === cat ? s.active : ""
            }`}
            onClick={() => scrollToCategory(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Sticky Tabs with animation */}
      <div className={`${s.categoryTabsSticky} ${isSticky ? s.show : ""}`}>
        <div className={s.categoryTabsStickyContent}>
          <div className={s.innerStickyWrapper}>
            {categories.map((cat) => (
              <button
                key={cat}
                className={`${s.tabButton} ${
                  activeCategory === cat ? s.active : ""
                }`}
                onClick={() => scrollToCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grouped Category Display */}
      {Object.entries(grouped).map(([category, items]) => (
        <div
          key={category}
          ref={(el) => (categoryRefs.current[category] = el)}
          className={s.categoryBlock}
        >
          <h3 className={s.categoryTitle}>{category}</h3>
          <ul className={s.list}>
            {items?.map((product, idx) =>
              theme === "without_image" ? (
                <ProductWithoutImage key={idx} product={product} />
              ) : (
                <ProductWithImage
                  key={idx}
                  idx={idx}
                  product={product}
                  expandedIndex={expandedIndex}
                  setExpandedIndex={setExpandedIndex}
                />
              ),
            )}
          </ul>
        </div>
      ))}

      <hr className={s.shopDivider} />
    </div>
  );
}
